'use client'

import { useRef } from 'react'
import AuthBrain from '@/components/auth/AuthBrain'
import { cinzel } from '@/lib/brand-font'

// Marketing landing page (public). Authed users are redirected to /note/home
// by the middleware before this component ever renders.
//
// Hero = the same OMNEX I brain intro as /login (AuthBrain in hero mode, with
// the real sign-in modal), and the marketing sections scroll below it. The
// page scrolls inside its own container because the global html/body has
// overflow:hidden for the app shell.
export default function MarketingPage() {
  const scrollRef = useRef<HTMLDivElement>(null)

  const openSignIn = () => { window.location.href = '/login' }

  // The big Omnex pillars, presented in the order a new customer cares about.
  const features: { icon: string; title: string; sub: string }[] = [
    { icon: '❖', title: 'Pages & Knowledge', sub: 'A beautiful editor with backlinks, tags, graph view and semantic search. Replaces Notion.' },
    { icon: '✓', title: 'Tasks & Kanban', sub: 'Priorities, recurring tasks, My Day, Eisenhower matrix and full Kanban boards. Replaces Todoist + Trello.' },
    { icon: '◎', title: 'Calendar & Booking', sub: 'Month, week and agenda views — plus public booking pages people can schedule you on. Replaces Fantastical + Calendly.' },
    { icon: '▦', title: 'Tables & Prognosis', sub: 'Spreadsheet tables with formulas, six chart types and trend forecasts of where your numbers are heading. Replaces Sheets.' },
    { icon: '✦', title: 'Your Own AI', sub: 'Bring your own assistant — a Claude/ChatGPT key or a local model. It only ever sees your pages. Private by design.' },
    { icon: '⧉', title: 'Templates', sub: 'Ready-made pages — meeting notes, OKRs, CRM, habit trackers — with premium designs on higher plans.' },
    { icon: '◇', title: 'Canvas & Profiles', sub: 'Infinite whiteboards and public link-in-bio pages. Replaces Miro + Linktree.' },
    { icon: '⚭', title: 'Team & Community', sub: 'Invite your team with roles and plans, share pages publicly, and collaborate in a shared wiki.' },
  ]

  return (
    <div className="mkt" ref={scrollRef}>
      {/* HERO — the same golden neural brain as /login; Enter sits top-right */}
      <section className="mkt-hero-wrap">
        <AuthBrain hero ctaLabel="Enter" onCta={openSignIn} />
        <button
          className="mkt-scroll-hint"
          onClick={() => scrollRef.current?.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })}
          aria-label="Scroll down"
        >
          <span>Scroll down</span>
          <span className="mkt-scroll-chevron">⌄</span>
        </button>
      </section>

      {/* FEATURES */}
      <section className="mkt-section">
        <div className="mkt-section-inner">
          <div className="mkt-section-eyebrow">Features</div>
          <h2 className={`mkt-section-title ${cinzel.className}`}>Everything in one place.</h2>
          <div className="mkt-features-grid">
            {features.map((f) => (
              <div key={f.title} className="mkt-feature">
                <div className="mkt-feature-icon" aria-hidden="true">{f.icon}</div>
                <div className="mkt-feature-title">{f.title}</div>
                <div className="mkt-feature-sub">{f.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="mkt-section mkt-philosophy">
        <div className="mkt-section-inner">
          <blockquote className="mkt-quote">
            <span className="mkt-quote-mark">{'“'}</span>
            Your knowledge. Your server. Your rules.
            <span className="mkt-quote-mark close">{'”'}</span>
          </blockquote>
          <p className="mkt-philosophy-sub">
            Omnex runs on your own infrastructure. Notes never leave the server you control.
            Built in the EU, designed for full data residency, and shaped by Finnish minimalism —
            quiet, deliberate, and uncompromising about what stays private.
          </p>
        </div>
      </section>

      {/* PRIVACY BLOCK */}
      <section className="mkt-section">
        <div className="mkt-section-inner">
          <div className="mkt-privacy-grid">
            <div className="mkt-privacy-col">
              <div className="mkt-privacy-icon">{'⧈'}</div>
              <div className="mkt-privacy-title">Self-hosted</div>
              <div className="mkt-privacy-sub">One container, one volume, one binary. No SaaS lock-in. Move it whenever you want.</div>
            </div>
            <div className="mkt-privacy-col">
              <div className="mkt-privacy-icon">{'⚷'}</div>
              <div className="mkt-privacy-title">Encrypted</div>
              <div className="mkt-privacy-sub">Protected pages use AES-256-GCM at rest. Public links can be locked behind a bcrypt access code.</div>
            </div>
            <div className="mkt-privacy-col">
              <div className="mkt-privacy-icon">{'∅'}</div>
              <div className="mkt-privacy-title">No tracking</div>
              <div className="mkt-privacy-sub">Zero analytics. Zero third-party scripts. The only people who see your data are the ones you give the link to.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ENTER CTA */}
      <section className="mkt-section mkt-final">
        <div className="mkt-section-inner mkt-final-inner">
          <div className="mkt-final-eyebrow">Ready?</div>
          <h2 className={`mkt-final-title ${cinzel.className}`}>Enter Omnex</h2>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="mkt-cta lg" onClick={openSignIn}>
              Enter
            </button>
            <button className="mkt-cta lg" onClick={() => { window.location.href = '/register' }}>
              Request access
            </button>
          </div>
        </div>
      </section>

      <footer className="mkt-foot">
        <span className="mkt-foot-mark">{"✦"}</span>
        <span className={cinzel.className}>OMNEX</span> <span className="mkt-foot-sep">{'·'}</span>
        Personal Knowledge Architecture
      </footer>

      <style jsx>{`
        .mkt {
          background: var(--bg-deep);
          color: var(--t1);
          font-family: var(--font-body);
          /* The global html/body has overflow:hidden (app shell), so the
             marketing page scrolls inside its own full-height container. */
          height: 100vh;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* — Hero (brain) — */
        .mkt-hero-wrap {
          position: relative;
          height: 100vh;
          min-height: 640px;
          width: 100%;
          overflow: hidden;
        }

        .mkt-scroll-hint {
          position: absolute;
          bottom: 12px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 210;
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          font-size: 11px;
          font-weight: 600;
          color: #e8b474;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          text-shadow: 0 0 16px rgba(232, 180, 116, 0.4);
          /* Appear after the splash dissolves, then blink invitingly */
          opacity: 0;
          animation:
            mkt-hint-in 1.4s ease 7s forwards,
            mkt-scroll-blink 1.6s ease-in-out 8.4s infinite;
        }
        @keyframes mkt-hint-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .mkt-scroll-chevron {
          font-size: 24px;
          line-height: 0.7;
          color: #f2c78e;
          animation: mkt-chevron-bob 1.6s ease-in-out infinite;
        }
        @keyframes mkt-scroll-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        @keyframes mkt-chevron-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(5px); }
        }

        /* — Sections — */
        .mkt-section {
          padding: 110px 24px;
          border-top: 1px solid rgba(201, 162, 78, 0.06);
        }
        .mkt-section-inner {
          max-width: 980px;
          margin: 0 auto;
        }
        .mkt-section-eyebrow {
          font-size: 11px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: var(--gold);
          opacity: 0.7;
          margin-bottom: 14px;
        }
        .mkt-section-title {
          font-family: var(--font-display);
          font-size: clamp(26px, 4vw, 40px);
          font-weight: 500;
          color: var(--t1);
          margin: 0 0 42px;
        }

        .mkt-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 14px;
        }
        .mkt-feature {
          border: 1px solid rgba(201, 162, 78, 0.09);
          border-radius: 12px;
          padding: 26px 24px;
          background: rgba(255, 255, 255, 0.012);
          transition: border-color 200ms ease, transform 200ms ease;
        }
        .mkt-feature:hover {
          border-color: rgba(201, 162, 78, 0.28);
          transform: translateY(-2px);
        }
        .mkt-feature-icon {
          font-size: 20px;
          color: var(--gold);
          opacity: 0.85;
          margin-bottom: 14px;
        }
        .mkt-feature-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--t1);
          margin-bottom: 6px;
        }
        .mkt-feature-sub {
          font-size: 13px;
          line-height: 1.55;
          color: var(--t2);
        }

        /* — Philosophy — */
        .mkt-philosophy {
          text-align: center;
        }
        .mkt-quote {
          font-family: var(--font-display);
          font-size: clamp(24px, 3.6vw, 38px);
          font-weight: 500;
          color: var(--t1);
          margin: 0 auto 22px;
          max-width: 720px;
        }
        .mkt-quote-mark {
          color: var(--gold);
          opacity: 0.7;
          margin-right: 6px;
        }
        .mkt-quote-mark.close { margin-left: 6px; margin-right: 0; }
        .mkt-philosophy-sub {
          max-width: 620px;
          margin: 0 auto;
          font-size: 14px;
          line-height: 1.7;
          color: var(--t2);
        }

        /* — Privacy — */
        .mkt-privacy-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 40px;
          text-align: center;
        }
        .mkt-privacy-icon {
          font-size: 22px;
          color: var(--gold);
          opacity: 0.85;
          margin-bottom: 12px;
        }
        .mkt-privacy-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--t1);
          margin-bottom: 8px;
        }
        .mkt-privacy-sub {
          font-size: 13px;
          line-height: 1.6;
          color: var(--t2);
          max-width: 300px;
          margin: 0 auto;
        }

        /* — Final CTA — */
        .mkt-final { text-align: center; }
        .mkt-final-eyebrow {
          font-size: 11px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: var(--gold);
          opacity: 0.7;
          margin-bottom: 12px;
        }
        .mkt-final-title {
          font-family: var(--font-display);
          font-size: clamp(28px, 4.4vw, 46px);
          font-weight: 500;
          color: var(--t1);
          margin: 0 0 34px;
        }
        .mkt-cta {
          background: none;
          border: 1px solid rgba(201, 162, 78, 0.45);
          color: var(--gold);
          font-family: var(--font-display);
          font-size: 13px;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          padding: 16px 54px;
          cursor: pointer;
          transition: background 250ms ease, border-color 250ms ease;
        }
        .mkt-cta:hover {
          background: rgba(201, 162, 78, 0.08);
          border-color: rgba(201, 162, 78, 0.8);
        }
        .mkt-cta.lg {
          padding: 18px 64px;
          font-size: 14px;
        }

        .mkt-foot {
          padding: 34px 24px 44px;
          text-align: center;
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--t3);
          border-top: 1px solid rgba(201, 162, 78, 0.06);
        }
        .mkt-foot-mark { color: var(--gold); margin-right: 8px; }
        .mkt-foot-sep { margin: 0 8px; opacity: 0.5; }
      `}</style>
    </div>
  )
}
